import { setGA4Event } from "@iewc/Store/Handlers/Utilities/CustomTracking";
import { colorStrings, mediaQueriesBP } from "@iewc/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { createShipTo } from "@insite/client-framework/Services/CustomersService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setCurrentShipTo from "@insite/client-framework/Store/Context/Handlers/SetCurrentShipTo";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getCurrentShipToState, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import loadShipTos from "@insite/client-framework/Store/Pages/Addresses/Handlers/LoadShipTos";
import updateShipTo from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateShipTo";
import createOrRequestQuote from "@insite/client-framework/Store/Pages/RfqRequestQuote/Handlers/CreateOrRequestQuote";
import updateQuoteParameter from "@insite/client-framework/Store/Pages/RfqRequestQuote/Handlers/UpdateQuoteParameter";
import translate from "@insite/client-framework/Translate";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import CustomerAddressForm, { CustomerAddressFormStyles } from "@insite/content-library/Components/CustomerAddressForm";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const settings = getSettingsCollection(state);

    const location = getLocation(state);
    const { cartId } = parseQueryString<{ cartId?: string }>(location.search);
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;

    return {
        customerSettings: settings.customerSettings,
        currentBillTo: getCurrentBillToState(state).value,
        currentShipTo: getCurrentShipToState(state).value,
        newShipTo: getCurrentShipToState(state).value,
        countries: getCurrentCountries(state),
        billToAddressFields: getAddressFieldsDataView(state).value?.billToAddressFields,
        cart,
        shipToState: getShipToState(state, cart ? cart.shipToId : undefined),
        rfqConfirmationGuestPage: getPageLinkByPageType(state, "RfqConfirmationGuestPage")?.url,
        rfqConfirmationPage: getPageLinkByPageType(state, "RfqConfirmationPage")?.url,
        notFoundErrorPageUrl: getPageLinkByPageType(state, "NotFoundErrorPage")?.url,
    };
};

const mapDispatchToProps = {
    createShipTo,
    setCurrentShipTo,
    loadCurrentCart,
    loadShipTos,
    updateShipTo,
    createOrRequestQuote,
    updateQuoteParameter,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface GuestAddressesStyles {
    loadingWrapper?: InjectableCss;
    container?: GridContainerProps;
    shippingAddressGridItem?: GridItemProps;
    customerAddressFormStyles?: CustomerAddressFormStyles;
    flowIndicatorGridItem?: GridItemProps;
    shippingAddressTitleGridItem?: GridItemProps;
}

export const guestAddressesStyles: GuestAddressesStyles = {
    container: {
        gap: 0,
        css: css`
            margin-block: 20px;
        `,
        offsetCss: css`
            display: flex;
            flex-direction: column;
            gap: 10px;
            justify-content: space-between;
        `,
    },

    loadingWrapper: {
        css: css`
            display: flex;
            height: 450px;
            justify-content: center;
            align-items: center;
        `,
    },

    flowIndicatorGridItem: {
        width: 12,
        css: css`
            ul.progress {
                display: none;

                ${mediaQueriesBP.minMD} {
                    list-style: none;
                    padding: 0;
                    overflow: hidden;
                    width: 100%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin: 30px 0;
                    height: 40px;
                }
            }

            ul.progress li {
                width: 50%;
                text-align: center;
                color: ${getColor(colorStrings.textMain)};
                background-color: ${getColor(colorStrings.commonGlobalWhite)};
                border: solid 1px ${getColor(colorStrings.commonBorder)};

                position: relative;

                p {
                    margin: 0;
                    font-size: 14px;
                    line-height: 38px;
                }

                &.active {
                    background-color: ${getColor(colorStrings.secondaryMain)};
                    border: solid 1px ${getColor(colorStrings.secondaryMain)};

                    p {
                        color: ${getColor(colorStrings.commonGlobalWhite)};
                    }

                    &::after {
                        content: "";
                        position: absolute;
                        right: -3px;
                        width: 30px;
                        padding-top: 1.2em;
                        border: 10px solid ${getColor(colorStrings.secondaryMain)};
                        z-index: 1;
                        transform: rotate(45deg) translateY(-65%);
                        border-bottom: 0;
                        border-left: 0;
                        box-shadow: 0;
                        top: 50%;
                        height: 30px;
                        background: ${getColor(colorStrings.secondaryMain)};
                    }
                }
            }
        `,
    },

    shippingAddressTitleGridItem: {
        width: 12,
    },

    customerAddressFormStyles: {
        formFields: {
            lastNameGridItem: {
                width: 12,
            },
            postalCodeGridItem: {
                width: 12,
            },
            phoneGridItem: {
                width: 12,
            },
            companyNameGridItem: {
                width: 12,
            },
            firstNameGridItem: {
                width: 12,
            },
            stateGridItem: {
                width: 12,
            },
        },
    },

    shippingAddressGridItem: { width: [12, 12, 12, 6, 6] },
};

const styles = guestAddressesStyles;

const RfqRequestQuoteGuestAddressForm: FC<Props> = ({
    currentBillTo,
    newShipTo,
    countries,
    billToAddressFields,
    cart,
    loadCurrentCart,
    updateShipTo,
    history,
    rfqConfirmationGuestPage,
    rfqConfirmationPage,
    notFoundErrorPageUrl,
    createOrRequestQuote,
    updateQuoteParameter,
}) => {
    const [newShiptoClone, setNewShiptoClone] = useState<ShipToModel | undefined>(undefined);

    useEffect(() => {
        if (!cart) {
            loadCurrentCart({ shouldLoadFullCart: true });
        }
    }, []);

    const saveParameter = () => {
        // we retrieve the cart notes from local storage and update the quote parameter
        const cartNotes = localStorage.getItem(`quoteNotes`);

        updateQuoteParameter({
            quoteId: cart?.id || "",
            quoteType: "quote",
            accountId: undefined,
            jobName: "",
            note: cartNotes ?? "",
        });
    };
    useEffect(saveParameter, [cart?.id]);

    const formSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: ShipToModel) => {
        if (!currentBillTo || !newShipTo) {
            return;
        }

        setGA4Event(cart!.cartLines ?? [], "request_quote");
        // Valtech Custom Code
        setGA4Event(cart?.cartLines || [], "submit_quote_request", { billtoNumber: currentBillTo?.customerNumber?.replace(/^0+/, "") });

        updateShipTo({
            billToId: currentBillTo.id,
            shipTo: address,
            isEdit: true,
            onSuccess: () => {
                // Custom Override for Guest, BE creates the quote and returns the quoteId
                createOrRequestQuote({
                    onSuccess: (result: any) => {
                        if (result?.CartId !== "undefined" || result?.CartId !== undefined) {
                            history.push(`${rfqConfirmationGuestPage}?quoteId=${result["cartId"]}`);
                        } else {
                            history.push(`${notFoundErrorPageUrl}`);
                        }
                    },
                });
            },
        });
    };

    useEffect(() => {
        if (newShipTo) {
            // we deep clone the newShipTo so that we can modify it without affecting the original
            const clone = JSON.parse(JSON.stringify(newShipTo));
            // we replace newShipToClone validation with the currentBillTo validation ones
            clone.validation = currentBillTo?.validation;
            clone.isNew = true;
            clone.oneTimeAddress = false;

            setNewShiptoClone(clone);
        }
    }, [newShipTo]);

    if (!cart || !newShipTo || !countries || !billToAddressFields) {
        return (
            <StyledWrapper {...styles.loadingWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.flowIndicatorGridItem}>
                <ul className="progress">
                    <li className="active">
                        <Typography variant="p">{translate("Shipping")}</Typography>
                    </li>
                    <li>
                        <Typography variant="p">{translate("Confirmation")}</Typography>
                    </li>
                </ul>
            </GridItem>
            <GridItem {...styles.shippingAddressTitleGridItem}>
                <Typography variant="h3">Shipping Address</Typography>
            </GridItem>
            <GridItem {...styles.shippingAddressGridItem}>
                {newShiptoClone && countries && billToAddressFields && (
                    <CustomerAddressForm
                        address={newShiptoClone}
                        countries={countries}
                        addressFieldDisplayCollection={billToAddressFields}
                        onSubmit={formSubmitHandler}
                        extendedStyles={styles.customerAddressFormStyles}
                        saveButtonTextOverride={translate("Submit Quote Request")}
                    />
                )}
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(RfqRequestQuoteGuestAddressForm)),
    definition: {
        group: "RFQ Request Quote",
        displayName: "Request Quote Guest Address Form",
        allowedContexts: ["RfqRequestQuoteGuestAddressPage" as any],
        fieldDefinitions: [],
    },
};

export default widgetModule;
